import { IProduct } from '@models/IProduct';
import { ListingProductType } from '@models/IProductListing';
import { IProductListingStatusLabels } from '@models/IProductListingStatusLabels';
import ListingCardTypeEnum from '@models/enums/ListingCardTypeEnum';
import chunkArray from '@utils/helpers/chunkArray';
import updateDataLayer from '@utils/helpers/gtm';
import mapToNewTrackingData from '@utils/helpers/mapToNewTrackingData';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import ProductListingCardRow from './ProductListingCardRow';

interface Props {
  readonly productListingName: string;
  readonly results: ListingProductType[];
  readonly statusLabels: IProductListingStatusLabels;
  readonly pageSize: number;
  readonly pageNumber: number;
  readonly showOnlyKits?: boolean;
  readonly sortByParam?: string;
}

const ProductListingCardBlock = ({
  productListingName,
  pageSize,
  pageNumber,
  results,
  statusLabels,
  showOnlyKits,
  sortByParam,
}: Props) => {
  const isMobile = useMediaQuery(1024);
  const alreadyTrackedProducts = useRef<string[]>([]);
  const [activeCard, setActiveCard] = useState<IProduct | null>(null);
  const [activeColumnIndex, setActiveColumnIndex] = useState<number | null>(
    null
  );
  const [activeRowIndex, setActiveRowIndex] = useState<number | null>(null);
  const chunkedResults = useMemo(
    () => chunkArray(results, isMobile ? 1 : 3),
    [isMobile, results]
  );
  const nextFocusIndex = pageSize * pageNumber;

  const setCard = ({
    rowIndex,
    columnIndex,
  }: {
    rowIndex: number;
    columnIndex: number;
  }) => {
    setActiveCard(
      (chunkedResults[rowIndex]?.[columnIndex] as IProduct) || null
    );
  };

  const handleActiveCardReset = () => {
    setActiveCard(null);
    setActiveRowIndex(null);
    setActiveColumnIndex(null);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Additional Dependencies
  useEffect(() => {
    // The logic below is used when we switch between mobile and desktop layouts
    // (e.g. by zooming in) so the kits dropdown remains open for the same product card
    if (activeRowIndex === null || activeColumnIndex === null) return;

    const updatedActiveRowIndex = isMobile
      ? activeRowIndex * 3 + activeColumnIndex
      : Math.floor(activeRowIndex / 3);
    const updatedActiveColumnIndex = isMobile
      ? 0
      : activeRowIndex - 3 * updatedActiveRowIndex;

    setActiveRowIndex(updatedActiveRowIndex);
    setActiveColumnIndex(updatedActiveColumnIndex);
    setCard({
      rowIndex: updatedActiveRowIndex,
      columnIndex: updatedActiveColumnIndex,
    });
  }, [isMobile]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Missing Dependencies
  useEffect(() => {
    if (pageNumber === 0) {
      handleActiveCardReset();
    }
  }, [showOnlyKits, sortByParam, results]);

  const handleDesktopKitDropdownButtonClick = (
    rowIndex: number,
    columnIndex: number
  ) => {
    const isActiveCard =
      activeColumnIndex === columnIndex && activeRowIndex === rowIndex;

    setCard({ rowIndex, columnIndex });
    setActiveColumnIndex(isActiveCard ? null : columnIndex);
    setActiveRowIndex(isActiveCard ? null : rowIndex);
  };

  const handleMobileKitDropdownButtonClick = (rowIndex: number) => {
    const isActiveCard = activeColumnIndex === 0 && activeRowIndex === rowIndex;

    setCard({ rowIndex, columnIndex: 0 });
    setActiveColumnIndex(isActiveCard ? null : 0);
    setActiveRowIndex(activeRowIndex === rowIndex ? null : rowIndex);
  };

  const handleListingProductsTracking = (
    currentProducts: ListingProductType[]
  ) => {
    const newTrackedProducts = currentProducts.filter(
      (product) =>
        product.type === ListingCardTypeEnum.Product &&
        product.trackingData &&
        !alreadyTrackedProducts.current.includes(product.sku ?? '')
    ) as IProduct[];

    alreadyTrackedProducts.current = [
      ...alreadyTrackedProducts.current,
      ...newTrackedProducts.map((product) => product.sku ?? ''),
    ];
    if (newTrackedProducts.length === 0) return;

    updateDataLayer({
      ecommerce: null,
    });
    updateDataLayer({
      event: 'viewItemList',
      ecommerce: {
        item_list_name: productListingName,
        items: newTrackedProducts.map((product) =>
          mapToNewTrackingData(product.trackingData!, {
            listingName: productListingName,
          })
        ),
      },
    });
  };

  return (
    <div className={classNames('mt-12 space-y-8 md:space-y-20')}>
      {chunkedResults.map((rowProducts, rowIndex) => (
        <div
          key={rowProducts.map((product) => product.title).join(',')}
          className={classNames({
            'pb-14 lg:pb-[150px]': rowIndex === chunkedResults.length - 1,
          })}
        >
          <ProductListingCardRow
            activeCard={activeCard}
            activeColumnIndex={activeColumnIndex}
            activeRowIndex={activeRowIndex}
            chunkedResults={chunkedResults}
            nextFocusIndex={nextFocusIndex}
            products={rowProducts}
            productListingName={productListingName}
            rowIndex={rowIndex}
            statusLabels={statusLabels}
            onDesktopKitDropdownButtonClick={
              handleDesktopKitDropdownButtonClick
            }
            onMobileKitDropdownButtonClick={handleMobileKitDropdownButtonClick}
            onListingProductsTracking={handleListingProductsTracking}
            onCloseDropdown={handleActiveCardReset}
          />
        </div>
      )) || null}
    </div>
  );
};

export default ProductListingCardBlock;
